import { createSlice } from '@reduxjs/toolkit';
import {
  createPortfolioThunk,
  fetchPortfoliosThunk,
  fetchPortfolioByIdThunk,
  updatePortfolioThunk,
  deletePortfolioThunk,
  bulkCreateStocksThunk,
  fetchStocksByPortfolioIdThunk,
  updateStockThunk,
  deleteStockThunk,
  createAnalyticsThunk,
  fetchAnalyticsByPortfolioIdThunk,
  updateAnalyticsThunk,
} from './portfolioActionThunks';

const initialState = {
  portfolios: [],
  selectedPortfolio: null,
  stocks: [],
  analytics: null,
  loading: {
    portfolios: false,
    stocks: false,
    analytics: false
  },
  error: null,
};

const portfolioSlice = createSlice({
  name: 'portfolio',
  initialState,
  reducers: {
    resetError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    // Portfolio Handlers
    builder
      // Create Portfolio
      .addCase(createPortfolioThunk.pending, (state) => {
        state.loading.portfolios = true;
        state.error = null;
      })
      .addCase(createPortfolioThunk.fulfilled, (state, action) => {
        state.portfolios = [...state.portfolios, action.payload];
        state.loading.portfolios = false;
      })
      .addCase(createPortfolioThunk.rejected, (state, action) => {
        state.loading.portfolios = false;
        state.error = action.payload;
      })

      // Fetch All Portfolios
      .addCase(fetchPortfoliosThunk.pending, (state) => {
        state.loading.portfolios = true;
        state.error = null;
      })
      .addCase(fetchPortfoliosThunk.fulfilled, (state, action) => {
        state.portfolios = action.payload;
        state.loading.portfolios = false;
      })
      .addCase(fetchPortfoliosThunk.rejected, (state, action) => {
        state.loading.portfolios = false;
        state.error = action.payload;
      })

      // Fetch Single Portfolio
      .addCase(fetchPortfolioByIdThunk.pending, (state) => {
        state.loading.portfolios = true;
        state.error = null;
      })
      .addCase(fetchPortfolioByIdThunk.fulfilled, (state, action) => {
        state.selectedPortfolio = action.payload;
        state.loading.portfolios = false;
      })
      .addCase(fetchPortfolioByIdThunk.rejected, (state, action) => {
        state.loading.portfolios = false;
        state.error = action.payload;
      })

      // Update Portfolio
      .addCase(updatePortfolioThunk.pending, (state) => {
        state.loading.portfolios = true;
        state.error = null;
      })
      .addCase(updatePortfolioThunk.fulfilled, (state, action) => {
        state.portfolios = state.portfolios.map(portfolio =>
          portfolio.id === action.payload.id ? action.payload : portfolio
        );
        if (state.selectedPortfolio?.id === action.payload.id) {
          state.selectedPortfolio = action.payload;
        }
        state.loading.portfolios = false;
      })
      .addCase(updatePortfolioThunk.rejected, (state, action) => {
        state.loading.portfolios = false;
        state.error = action.payload;
      })

      // Delete Portfolio
      .addCase(deletePortfolioThunk.pending, (state) => {
        state.loading.portfolios = true;
        state.error = null;
      })
      .addCase(deletePortfolioThunk.fulfilled, (state, action) => {
        state.portfolios = state.portfolios.filter(portfolio => portfolio.id !== action.payload);
        if (state.selectedPortfolio?.id === action.payload) {
          state.selectedPortfolio = null;
        }
        state.loading.portfolios = false;
      })
      .addCase(deletePortfolioThunk.rejected, (state, action) => {
        state.loading.portfolios = false;
        state.error = action.payload;
      })

      // Stock Handlers
      .addCase(bulkCreateStocksThunk.pending, (state) => {
        state.loading.stocks = true;
        state.error = null;
      })
      .addCase(bulkCreateStocksThunk.fulfilled, (state, action) => {
        state.stocks = [...state.stocks, ...action.payload];
        state.loading.stocks = false;
      })
      .addCase(bulkCreateStocksThunk.rejected, (state, action) => {
        state.loading.stocks = false;
        state.error = action.payload;
      })

      .addCase(fetchStocksByPortfolioIdThunk.pending, (state) => {
        state.loading.stocks = true;
        state.error = null;
      })
      .addCase(fetchStocksByPortfolioIdThunk.fulfilled, (state, action) => {
        state.stocks = action.payload;
        state.loading.stocks = false;
      })
      .addCase(fetchStocksByPortfolioIdThunk.rejected, (state, action) => {
        state.loading.stocks = false;
        state.error = action.payload;
      })

      .addCase(updateStockThunk.pending, (state) => {
        state.loading.stocks = true;
        state.error = null;
      })
      .addCase(updateStockThunk.fulfilled, (state, action) => {
        state.stocks = state.stocks.map(stock =>
          stock.id === action.payload.id ? action.payload : stock
        );
        state.loading.stocks = false;
      })
      .addCase(updateStockThunk.rejected, (state, action) => {
        state.loading.stocks = false;
        state.error = action.payload;
      })

      .addCase(deleteStockThunk.pending, (state) => {
        state.loading.stocks = true;
        state.error = null;
      })
      .addCase(deleteStockThunk.fulfilled, (state, action) => {
        state.stocks = state.stocks.filter(stock => stock.id !== action.payload);
        state.loading.stocks = false;
      })
      .addCase(deleteStockThunk.rejected, (state, action) => {
        state.loading.stocks = false;
        state.error = action.payload;
      })

      // Analytics Handlers
      .addCase(createAnalyticsThunk.pending, (state) => {
        state.loading.analytics = true;
        state.error = null;
      })
      .addCase(createAnalyticsThunk.fulfilled, (state, action) => {
        state.analytics = action.payload;
        state.loading.analytics = false;
      })
      .addCase(createAnalyticsThunk.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload;
      })

      .addCase(fetchAnalyticsByPortfolioIdThunk.pending, (state) => {
        state.loading.analytics = true;
        state.error = null;
      })
      .addCase(fetchAnalyticsByPortfolioIdThunk.fulfilled, (state, action) => {
        state.analytics = action.payload;
        state.loading.analytics = false;
      })
      .addCase(fetchAnalyticsByPortfolioIdThunk.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload;
      })

      .addCase(updateAnalyticsThunk.pending, (state) => {
        state.loading.analytics = true;
        state.error = null;
      })
      .addCase(updateAnalyticsThunk.fulfilled, (state, action) => {
        state.analytics = action.payload;
        state.loading.analytics = false;
      })
      .addCase(updateAnalyticsThunk.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload;
      });
  },
});

export const { resetError, resetState } = portfolioSlice.actions;
export default portfolioSlice.reducer;